import { useEffect, useRef, useState } from "react";
import { z } from "@/hooks/schema";
import { Form, useFormContext } from "@/components/Form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSelectSectors } from "@/Api/sectors";
import { getSelectPositions } from "@/Api/positions";
import { getSelectWorkSchedules } from "@/Api/workSchedules";

export const hrFieldsSchema = z.object({
  sector_id: z.string().optional(),
  position_id: z.string().optional(),
  work_schedule_id: z.string().optional(),
  admission_date: z.string().optional(),
  max_daily_overtime_minutes: z.string().optional(),
});

// Chaves opcionais que não devem ser enviadas ao backend como string vazia
// (exists:sectors,id/positions,id/work_schedules,id rejeita "").
export const HR_FIELDS_OMIT_WHEN_EMPTY = [
  "sector_id",
  "position_id",
  "work_schedule_id",
  "admission_date",
  "max_daily_overtime_minutes",
];

export const HrFieldsStep = ({ isOpen }: { isOpen: boolean }) => {
  const [sectors, setSectors] = useState([]);
  const [positions, setPositions] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);

  const { watch, setValue } = useFormContext();
  const unityId = watch?.("unity_id");
  const previousUnityId = useRef(unityId);

  // Setor/cargo/horário são cadastrados por unidade: ao trocar a unidade, os
  // valores escolhidos antes deixam de ser válidos.
  useEffect(() => {
    if (previousUnityId.current && previousUnityId.current !== unityId) {
      setValue?.("sector_id", "");
      setValue?.("position_id", "");
      setValue?.("work_schedule_id", "");
    }
    previousUnityId.current = unityId;
  }, [unityId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!unityId) {
      setSectors([]);
      setPositions([]);
      setWorkSchedules([]);
      return;
    }
    const loadData = async () => {
      const [sectorsResponse, positionsResponse, workSchedulesResponse] =
        await Promise.all([
          getSelectSectors(unityId),
          getSelectPositions(unityId),
          getSelectWorkSchedules(unityId),
        ]);
      setSectors(sectorsResponse.data?.response || []);
      setPositions(positionsResponse.data?.response || []);
      setWorkSchedules(workSchedulesResponse.data?.response || []);
    };
    loadData();
  }, [isOpen, unityId]);

  return (
    <>
      <Form.Row>
        <Form.Field name="sector_id">
          {({ field }) => (
            <Form.Item>
              <Form.Label>
                Setor <Form.Message />
              </Form.Label>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um setor" />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map((sector: { id: string; name: string }) => (
                    <SelectItem key={sector.id} value={String(sector.id)}>
                      {sector.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Form.Item>
          )}
        </Form.Field>
      </Form.Row>

      <Form.Row>
        <Form.Field name="position_id">
          {({ field }) => (
            <Form.Item>
              <Form.Label>
                Cargo <Form.Message />
              </Form.Label>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um cargo" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((position: { id: string; name: string }) => (
                    <SelectItem key={position.id} value={String(position.id)}>
                      {position.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Form.Item>
          )}
        </Form.Field>
      </Form.Row>

      <Form.Row>
        <Form.Field name="work_schedule_id">
          {({ field }) => (
            <Form.Item>
              <Form.Label>
                Horário de Trabalho <Form.Message />
              </Form.Label>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um horário" />
                </SelectTrigger>
                <SelectContent>
                  {workSchedules.map(
                    (workSchedule: { id: string; name: string }) => (
                      <SelectItem
                        key={workSchedule.id}
                        value={String(workSchedule.id)}
                      >
                        {workSchedule.name}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </Form.Item>
          )}
        </Form.Field>
      </Form.Row>

      <Form.Row>
        <Form.Field name="admission_date">
          {({ field }) => (
            <Form.Item>
              <Form.Label>
                Data de Admissão <Form.Message />
              </Form.Label>
              <Form.Control>
                <Input {...field} type="date" />
              </Form.Control>
            </Form.Item>
          )}
        </Form.Field>
      </Form.Row>

      <Form.Row>
        <Form.Field name="max_daily_overtime_minutes">
          {({ field }) => (
            <Form.Item>
              <Form.Label>
                Limite diário de hora extra (minutos)
              </Form.Label>
              <Form.Control>
                <Input
                  {...field}
                  type="number"
                  min={0}
                  max={600}
                  placeholder="Deixe em branco para herdar do setor"
                />
              </Form.Control>
            </Form.Item>
          )}
        </Form.Field>
      </Form.Row>
    </>
  );
};
